import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Tag, Calendar, Send } from 'lucide-react';
import { useFeaturedPosts } from '../../hooks/useBlogPosts';
import { BLOG_CATEGORIES } from '../../data/blog/categories';
import { formatDate } from '../../utils/format/date';

export function BlogSidebar() {
  const { data: featuredPosts = [] } = useFeaturedPosts(5);
  const [email, setEmail] = React.useState('');

  const popularTags = [
    'live-rates', 'black-market', 'dollar-naira', 'euro-naira', 
    'market-analysis', 'trading-tips', 'cbn-updates', 'forex-education'
  ];

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle newsletter subscription
    console.log('Newsletter signup:', email);
    setEmail('');
  };

  return (
    <div className="space-y-8">
      {/* Popular Posts */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Popular Posts</h3>
        </div>
        <div className="space-y-4">
          {featuredPosts.slice(0, 5).map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="block group"
            >
              <div className="flex gap-3">
                <img
                  src={post.featuredImage || 'https://images.unsplash.com/photo-1639322537228-f710d846310a'}
                  alt={post.title}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm line-clamp-2 group-hover:text-emerald-600 transition-colors">
                    {post.title}
                  </h4>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(post.publishedAt)}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Categories</h3>
        <div className="space-y-2">
          {BLOG_CATEGORIES.map((category) => (
            <Link
              key={category.id}
              to={`/blog/category/${category.slug}`}
              className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: category.color }}
                />
                <span className="text-sm group-hover:text-emerald-600 transition-colors">
                  {category.name}
                </span>
              </div>
              <span className="text-xs text-gray-500">
                {category.postCount}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* Popular Tags */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Tag className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Popular Tags</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {popularTags.map((tag) => (
            <Link
              key={tag}
              to={`/blog/tag/${tag}`}
              className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm 
                       hover:bg-emerald-100 hover:text-emerald-700 transition-colors"
            >
              #{tag}
            </Link>
          ))}
        </div>
      </div>

      {/* Newsletter Signup */}
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-2">Stay Updated</h3>
        <p className="text-sm text-gray-600 mb-4">
          Get the latest market insights and live Naira exchange rate today updates delivered to your inbox.
        </p>
        <form onSubmit={handleNewsletterSubmit} className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                     focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            required
          />
          <button
            type="submit"
            className="w-full bg-emerald-600 text-white py-2 rounded-lg 
                     hover:bg-emerald-700 transition-colors font-medium flex items-center justify-center gap-2"
          >
            <Send className="h-4 w-4" />
            Subscribe
          </button>
        </form>
      </div>
    </div>
  );
}